import { useStore } from '@store/appStore';
import persistentStore from '@store/persistentStore';
import moment from 'moment';
import { useI18n } from '@i18n/useI18n';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import db from '@utils/db';
import getIndex from '@utils/getIndex';
import { useListener } from 'react-bus';
import { AttendanceType } from '@models/AttendanceType';
import { ExtraMatchButtonType, MatchActionType } from '@models/MatchActionType';
import { Sorting } from '@models/ISorting';
import { useSearch } from '@providers/SearchProvider';
import RegistrationViewHandler from '@components/Registration/RegistrationViewHandler';
import { RegistrationViewType } from '@models/RegistrationViewType';
import AllDayMarkingArr from '@models/AllDayMarkingArr';
import { useAuthentication } from '@providers/AuthProvider';
import { useRouter } from 'next/router';
import { canSendWithToken } from '@utils/tokenRefresh';

const offsetMinutes = 10; // offset of the autoselect timeline item

// Checking whether someone else changed registrations on the lessons shown (Regi_CheckForChanges),
// which shows the update button - never a silent reload, the teacher may be mid-registration.
// The procedure looks back a given number of seconds, so each check asks for everything since the
// previous one (plus a small overlap) instead of a fixed 15s: with a fixed window, the request time
// between checks, browsers slowing timers in background tabs, or a sleeping laptop all left gaps in
// which other people's registrations never triggered the button.
const CHANGE_CHECK_INTERVAL_MS = 30 * 1000;
const CHANGE_CHECK_OVERLAP_SECONDS = 5;

const RegistrationHandler = () => {
  const {
    callNameSetting,
    schedule,
    setSchedule,
    setScheduleIndex,
    students,
    scheduleIndex,
    setStudents,
    client,
    updateRegistration,
    registrationIndex,
    setRegistrationIndex,
    setScheduleLoading,
    setStudentsLoading,
    setError,
    massUpdateRegistration,
    listSelection,
    selectedCount,
    updateListStudents,
    setTutorial,
    setSelectedStudent,
    filterDate,
    groups,
    systemSettings,
    selectedStudent,
    noteUserId,
    settings,
    setUpdateCheck,
    updateNow,
    setUpdateNow,
    setHighLightDates
  } = useStore();
  const i18n = useI18n();
  const { registrationQuery } = useSearch();
  const { getId, getType } = useAuthentication();
  const current = useRef(null);
  const { studentSort, urlQuery, setPersistentStore, registrationView } = persistentStore();
  // Pending change check while polling is active, null when it isn't (button shown / not today).
  const updater = useRef(null)
  // When the last successful check (or the data load) happened - the next check looks back to here.
  const lastChangeCheck = useRef<number>(Date.now())
  // Always the current handleUpdate, so a scheduled check doesn't run with the schedule/lesson that
  // was on screen when it was scheduled.
  const handleUpdateRef = useRef<() => Promise<void>>(null)
  const router = useRouter();

  useEffect(() => {
    // handleUpdate() re-schedules itself via setTimeout indefinitely while
    // there's nothing new to show, and its own router.pathname !== "/registration"
    // guard can't catch a navigation-away that happens *after* a timer is
    // already scheduled - it's a stale closure at that point, still holding
    // whatever pathname was current when it was scheduled. Cancel on unmount
    // so leaving this page actually stops the polling.
    return () => {
      if (updater.current) clearTimeout(updater.current);
    };
  }, []);

  useEffect(() => {
    // Background tabs don't check (see handleUpdate); when the tab is shown again, check at once -
    // the window then covers the whole time it was hidden.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && updater.current) {
        clearTimeout(updater.current);
        handleUpdateRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  const dateText = (
    moment().isSame(moment(registrationQuery.date), 'day')
      ? i18n.t('today')
      :  // if not, show specific day/month
      ` ${i18n.t('on')} ${moment(registrationQuery.date).format('DD/MM')}`
  ).toLowerCase();
  const timeout = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem('demo') === '1') {
      //setTutorial(true);
    }
  });

  // Listening for view change, if the view is changed to list/from list, update students to
  // force getting all the students for all timeblocks, otherwise don't touch the student data
  const regViewRef = useRef(persistentStore.getState().registrationView);
  useEffect(
    () =>
      persistentStore.subscribe((state) => {
        // probably shit way of doing this, but i want to be able to read this
        // value inside the function to get the student list
        const cachedRegView = regViewRef.current;
        if (cachedRegView !== RegistrationViewType.FULL_DAY) {
          if (state.registrationView === RegistrationViewType.FULL_DAY) {
            // console.log('going into fullday view, reload students');
            regViewRef.current = state.registrationView;
            updateStudents();
          }
        } else {
          regViewRef.current = state.registrationView;
          // console.log('going from alldayview, reload students');
          updateStudents();
        }
      }),
    [schedule.data, students.data],
  );

  useEffect(() => {
    if (students && students.data && students.data.length > 0) {

      let i = students.data.length >= registrationIndex + 1 ? registrationIndex : 0
      setSelectedStudent(
        students.data[i].firstname,
        students.data[i].sis_id,
        null,
        students.data[i].type === 3 ? students.data[i].duration : null,
      );

      if (listSelection && listSelection.data && selectedCount === 1 && registrationView === RegistrationViewType.FULL_DAY) {
        let listTemp = listSelection.data.find((item) => {
          return Object.values(item.reservations).includes(true);
        });
        setSelectedStudent(students.data.find((s) => s.sis_id === listTemp.sis_id).firstname, listTemp.sis_id);
      }
    }
  }, [students.data, listSelection]);

  // Upadtes Schedule Items
  useEffect(() => {
    setScheduleLoading(true);
    updateSchedule();
  }, [registrationQuery.date, registrationQuery, i18n.language]);

  // Upadtes Students
  useEffect(() => {
    if (!schedule.loading) updateStudents();
    // Updates function on index change, selectedDate, registrationQuery
  }, [schedule.loading, scheduleIndex, filterDate, i18n.language]);

  // Set Students for the list view, it's really different, so we're gonna do some weird frankensteining
  // for it, but that's there for later
  // async function updateListStudents() {
  //   let l = schedule?.data?.length;
  //   let combinedArr = [];
  //   for (let i = 0; i < l; i++) {
  //     const scheduleItem = schedule.data[i];
  //     const data = await db.Regi_GetStudentsByTimeBlockId(scheduleItem.id);
  //     if (!data) return;
  //     let k = data.length;
  //     for (let j = 0; j < k; j++) {
  //       const student = data[j];
  //       let index = combinedArr.findIndex((item) => item.sis_id === student.sis_id);
  //       if (index > -1) {
  //         combinedArr[index].courses.push({ itemId: scheduleItem.id, type: student.type });
  //         continue;
  //       }
  //       student.courses = [];
  //       student.courses.push({ itemId: scheduleItem.id, type: student.type });
  //       combinedArr.push(student);
  //     }
  //   }
  //   setStudents(combinedArr);
  // }

  async function updateSchedule() {
    let data = null;
    let highlightDates = null;
    // Reset Errors Before Looking up new schedules
    setError('schedule', null);
    setError('students', null);
    setStudentsLoading(true);
    setScheduleLoading(true);
    // If course search is selected

    const { forVal, forType, forName, forId, date, forSubject } = registrationQuery;
    // console.log('registrationQuery', registrationQuery);
    const formatted = moment(date);

    let teacherWindow = systemSettings.find(s => s.APP_Setting_ID === 10)
    let counselorWindow = systemSettings.find(s => s.APP_Setting_ID === 13)

    let registrationWindow = groups?.includes(client?.AdminGroupName)?counselorWindow.value:teacherWindow.value    

    let userinfo = null
    if (forVal) {  
      switch (forType) {
        case 'STUDENT':
          data = await db.Regi_GetCoursesByStudentName(forVal, formatted, settings.USEMERIT, settings.USEPAUSE);
          userinfo = await db.getUserInfo(forVal);
          if(userinfo.length > 0 && userinfo[0].student_id)
          highlightDates = await db.Regi_GetTimeblocksDatesByStudentID(userinfo[0].student_id, registrationWindow, false, false);
          break;
        case 'TEACHER':
          data = await db.Regi_GetCoursesByTeacherName(forVal, formatted);
          userinfo = await db.getUserInfo(forVal);
          if(userinfo.length > 0 && userinfo[0].teacher_id)
          highlightDates = await db.Regi_GetTimeblocksDatesByTeacherID(userinfo[0].teacher_id, registrationWindow);
          break;
        case 'COURSE':
          data = await db.Regi_GetCoursesByCourseName(forVal, formatted);
          highlightDates = await db.Regi_GetTimeblocksDatesByCourseID(forId, registrationWindow);
          break;
        case 'SUBJECT':
          data = await db.Regi_GetCoursesByCourseName(forVal, formatted);
          data = data.filter((a) => a.displayname === forSubject);
          break;
      }
      // If any of them fail
      if (!data || data.length === 0) {
        if (forType === 'COURSE') {
          // console.log('error getting course');
          setError('schedule', `${i18n.t('no-schedule-for-person')} ${i18n.t(forType.toLowerCase())} "${forName}" ${dateText}`);
          setScheduleLoading(false);
        } else if (forType === 'SUBJECT') {
          setError('schedule', `${i18n.t('no-schedule-for-person')} ${i18n.t(forType.toLowerCase())} "${forName}" ${dateText}`);
          setScheduleLoading(false);
        } else {
          let name = forName.split('-')
          setError('schedule', `${i18n.t('no-schedule-for-person')} ${i18n.t(forType.toLowerCase())} "${name.length > 1 ? name[1].trim() : forName}" ${dateText}`);
          setScheduleLoading(false);
        }
        // return;
      }
    } else {
      //If search isn't selected, do a default search
      data = await db.Regi_GetCoursesByTeacherName(client.UserName, moment(registrationQuery.date));
      let userinfo = await db.getUserInfo(client.UserName);
      if(userinfo.length > 0 && userinfo[0].teacher_id)
      highlightDates = await db.Regi_GetTimeblocksDatesByTeacherID(userinfo[0].teacher_id, registrationWindow);
      if (!data || data.length === 0) {
        setError('schedule', `${i18n.t('no-schedule-for-you-error')} ${dateText}`);
      }
    }

    // If schedule exists, do stuff with it
    // Kinda need to do it this way to avoid flashing
    if (data && data.length > 0) {
      handleScheduleChange(data);
    }
    // Set data regardless if it's null, empty or whatever, handle this per component
    setSchedule(data);
    setHighLightDates(highlightDates);
    if (forId) {
      // find id that matches the course id of an item in the schedule, if it matches, set scheduleIndex to the index of that item
      const index = data?.findIndex((item) => {
        return item.id.toString() === registrationQuery.forId.toString();
      });
      if (index !== -1) {
        setScheduleIndex(index);
      }
    }
  }

  function handleScheduleChange(data) {
    // Functions that should calculate stuff
    // e.g closest schedule item by time
    setClosestSchedule(data);
  }

  function setClosestSchedule(data) {
    if (!data) return;
    const closest = data?.reduce((prev, curr) => {
      const date = moment(curr.date).hours(curr.starttime.hours).minutes(curr.starttime.minutes);
      const dateUnix = moment(date).unix();
      const nowUnix = moment().unix();
      // getting an offset to switch to the next course if it's within
      // allowed offset
      const offset = (offsetMinutes + 1) * 60;
      if (dateUnix - offset <= nowUnix) {
        return curr;
      }
      return prev;
    });

    const index = data?.indexOf(closest);
    setScheduleIndex(index);
  }

  useEffect(() => {
    if (updateNow) updateStudents()
  }, [updateNow])

  async function updateStudents() {
    // Reset errors before looking up new students
    const sIndex = scheduleIndex;
    current.current = scheduleIndex;
    if (updateNow) setUpdateNow(false)
    if (updateSchedule) setUpdateCheck(false)    

    setStudentsLoading(true);
    setStudents(null);
    setError('students', null);
    if (updater.current) clearTimeout(updater.current)
    updater.current = null
    if (schedule && schedule.data && schedule.data[scheduleIndex] && schedule.data[scheduleIndex]?.date && moment(schedule.data[scheduleIndex].date).isSame(moment(), 'date')) {
      // Fresh data is being loaded now - changes from here on are what the checks look for.
      lastChangeCheck.current = Date.now()
      updater.current = setTimeout(() => handleUpdateRef.current?.(), CHANGE_CHECK_INTERVAL_MS)
    }


    

    // if there's no schedule data, don't even look up students, straight nuke the list
    if (sIndex !== current.current) return;
    if (schedule.data?.length > 0 && schedule.data[scheduleIndex]) {
      const id = schedule.data[scheduleIndex].id;
      let data = [];
      if (regViewRef.current === RegistrationViewType.FULL_DAY) {
        const forType = registrationQuery.forType ? registrationQuery.forType : 'TEACHER';
        const forVal = registrationQuery.forVal ? registrationQuery.forVal : client.UserName;
        const srcStudentList = await db.Regi_GetStudents4AllDayView(
          forVal,
          moment(registrationQuery.date),
          forType.toLowerCase(),
          client.UserName,
          getType(),
        );
        if (settings.USEMERIT || settings.USEPAUSE) {
          let reservationIds = []
          for (let i = 0; i < srcStudentList.length; i++) {
            const s = srcStudentList[i];
            if (!reservationIds.includes(s.reservation_id)) reservationIds.push(s.reservation_id)
          }

          let meritStudents = []
          let pauseStudents = []
          let notFullPauseStudents = []
          if (settings.USEMERIT) meritStudents = await db.GetMeritStudentByTimeBlocks(JSON.stringify(reservationIds.map(r => { return { reservation_id: r } })));
          if (settings.USEPAUSE) pauseStudents = await db.GetPauseStudentByTimeBlocks(JSON.stringify(reservationIds.map(r => { return { reservation_id: r } })))
          if (settings.USENOTFULLBLOCKPAUSE) notFullPauseStudents = await db.GetPause4TimeblocksAlldayView(JSON.stringify(reservationIds.map(r => { return { reservation_id: r } })), filterDate.from, filterDate.to)

          let temp = []
          for (let i = 0; i < notFullPauseStudents.length; i++) {
            const mS = notFullPauseStudents[i];
            let s = temp.find(f => f.studentID === mS.student_id)
            if (s) {
              s.reservations.push(mS.reservation_id)
            }
            else {
              temp.push({ studentID: mS.student_id, reservations: [mS.reservation_id] })
            }
          }


          for (let i = 0; i < meritStudents.length; i++) {
            const mS = meritStudents[i];
            let s = temp.find(f => f.studentID === mS.student_id)
            if (mS.remove) {
              if (s) {
                s.reservations.push(mS.reservation_id)
              }
              else {
                temp.push({ studentID: mS.student_id, reservations: [mS.reservation_id] })
              }
            }
          }

          for (let i = 0; i < pauseStudents.length; i++) {
            const mS = pauseStudents[i];
            let s = temp.find(f => f.studentID === mS.student_id)
            if (mS.remove) {
              if (s) {
                s.reservations.push(mS.reservation_id)
              }
              else {
                temp.push({ studentID: mS.student_id, reservations: [mS.reservation_id] })
              }
            }
          }

          // we get the list of students with coresponding reservation_id, so I merge them into one obj with
          // reservation_id's merged into an array
          if (srcStudentList) {
            data = srcStudentList.reduce((prev, curr) => {
              const index = prev.findIndex((item) => item.sis_id === curr.sis_id);
              let removeStudent = temp.find(rS => rS.studentID === curr.sis_id);
              if (index > -1) {
                if (!(removeStudent && removeStudent.reservations.includes(curr.reservation_id)))
                  prev[index].reservations.push({
                    id: curr.reservation_id,
                    type: curr.type,
                    selected: false,
                    initial_Method: curr.initial_Method,
                    tag_ids: curr.tag_ids
                  });
                return prev;
              }
              if (!(removeStudent && removeStudent.reservations.includes(curr.reservation_id)))
                curr.reservations = [{ id: curr.reservation_id, type: curr.type, selected: false, initial_Method: curr.initial_Method,tag_ids: curr.tag_ids }];
              else
                curr.reservations = []
              // probs not gonna need it, so just removing it to be safe
              delete curr.reservation_id;
              prev.push(curr);
              return prev;
            }, []);

            data = data.filter(s => s.reservations.length > 0)

          } else {
            setError('students', `${i18n.t(callNameSetting + '-no-students-error')}`);
          }
        }
        else {
          // we get the list of students with coresponding reservation_id, so I merge them into one obj with
          // reservation_id's merged into an array
          if (srcStudentList) {
            data = srcStudentList.reduce((prev, curr) => {
              const index = prev.findIndex((item) => item.sis_id === curr.sis_id);
              if (index > -1) {
                prev[index].reservations.push({
                  id: curr.reservation_id,
                  type: curr.type,
                  selected: false,
                  initial_Method: curr.initial_Method,
                  tag_ids: curr.tag_ids
                });
                return prev;
              }
              curr.reservations = [{ id: curr.reservation_id, type: curr.type, selected: false, initial_Method: curr.initial_Method, tag_ids: curr.tag_ids }];
              // probs not gonna need it, so just removing it to be safe
              delete curr.reservation_id;
              prev.push(curr);
              return prev;
            }, []);

          } else {
            setError('students', `${i18n.t(callNameSetting + '-no-students-error')}`);
          }
        }

      } else {
        data = await db.Regi_GetStudentsByTimeBlockId(id, client.UserName, getType(), filterDate.from, filterDate.to);

        if (settings.USEMERIT || settings.USEPAUSE) {
          let meritStudents = []
          let pauseStudents = []
          let notFullPauseStudents = []
          if (settings.USEMERIT) meritStudents = await db.GetMeritStudentByTimeBlocks(JSON.stringify([{ reservation_id: id }]));
          if (settings.USEPAUSE) pauseStudents = await db.GetPauseStudentByTimeBlocks(JSON.stringify([{ reservation_id: id }]))
          if (settings.USENOTFULLBLOCKPAUSE) notFullPauseStudents = await db.GetPause4Timeblock(id, filterDate.from, filterDate.to)

          let calculateStudents = []
          let removeIds = []

          for (let i = 0; i < notFullPauseStudents.length; i++) {
            const student = notFullPauseStudents[i];
            if (student.full_block) removeIds.push(student.student_id)
            else {
              let s = data.find(s => s.sis_id === student.student_id)
              if (s) {
                s.total_duration = s.total_duration - student.total_pause_duration;
                s.total_absence_duration = s.total_absence_duration - student.total_pause_absence_length
                s.course_duration = s.course_duration - student.course_pause_duration
                s.course_absence_duration = s.course_absence_duration - student.course_pause_absence_length
                s.merit = true
              }
            }
          }

          for (let i = 0; i < meritStudents.length; i++) {
            const student = meritStudents[i];
            if (student.remove) {
              removeIds.push(student.student_id)
            }
            else {
              calculateStudents.push({ student_id: student.student_id })
            }
          }

          for (let i = 0; i < pauseStudents.length; i++) {
            const student = pauseStudents[i];
            if (student.remove) {
              removeIds.push(student.student_id)
            }
          }

          data = data.filter(s => !removeIds.includes(s.sis_id))

          if (settings.USEMERIT) {
            let meritData = await db.CalculateMeritStudentOnCourse(JSON.stringify(calculateStudents), filterDate.from, filterDate.to)
            for (let i = 0; i < meritData.length; i++) {
              const meritStudent = meritData[i];
              let student = data.find(s => s.sis_id === meritStudent.student_id)

              if (student) {
                student.total_duration = (student.total_duration - meritStudent.total_duration)
                student.total_absence_duration = (student.total_absence_duration - meritStudent.total_absence_duration)
                student.total_absence_lessons = (student.total_absence_lessons - meritStudent.total_absence_lessons)
                student.total_lessons = (student.total_lessons - meritStudent.total_lessons)
                student.merit = true
              }
            }
          }
        }

      }

      if (sIndex !== current.current) return;
      if (!moment(schedule.data[scheduleIndex].date).isSame(moment(registrationQuery.date))) return;
      if (!data || data.length === 0) {
        if (registrationQuery.forType === 'STUDENT') {
          setError(
            'students',
            `${i18n.t('no-schedule-for-person')} ${registrationQuery.forName} ${i18n.t('for')} ${schedule.data[scheduleIndex].displayname}`,
          );
        } else {
          setError('students', `${i18n.t(callNameSetting + '-no-students-error')} ${i18n.t('for')} ${schedule.data[scheduleIndex].displayname}`);
        }
      }
      if (sIndex !== current.current) return;
      if (!moment(schedule.data[scheduleIndex].date).isSame(moment(registrationQuery.date))) return;
      if (data && data.length > 0) {
        handleStudentChange(data);
        return;
      }

      // Set data regardless if it's null, empty or whatever, handle this per component
      setStudents(data);
      return;
    }

    setStudents(null);
    setError('students', `${i18n.t(callNameSetting + '-no-students-no-schedule-error')} ${dateText}`);
  }

  const getDateTime = (date, time) => {
    let d = moment(date)
    d = moment(d.format('YYYY-MM-DDT') + (time.hours < 10 ? "0" + time.hours : time.hours) + ":" + (time.minutes < 10 ? "0" + time.minutes : time.minutes))
    return d
  }

  async function handleUpdate() {
    clearTimeout(updater.current)
    updater.current = null
    if (router.pathname === "/" || router.pathname === "/registration") {
      if (schedule?.data && schedule.data[scheduleIndex]) {
        const scheduleNext = () => {
          updater.current = setTimeout(() => handleUpdateRef.current?.(), CHANGE_CHECK_INTERVAL_MS)
        }
        // Hidden tab: no request. The window keeps growing, and showing the tab checks at once.
        if (document.hidden) {
          scheduleNext()
          return
        }
        // Token expired and couldn't be renewed: the request would only be refused - same as hidden.
        if (!(await canSendWithToken())) {
          scheduleNext()
          return
        }

        let reservationIds = []
        if (registrationView === RegistrationViewType.FULL_DAY) reservationIds = schedule.data.map(s => { return { reservation_id: s.id } })
        else reservationIds = [{ reservation_id: schedule.data[scheduleIndex].id }]

        const checkStartedAt = Date.now()
        const lookBackSeconds = Math.ceil((checkStartedAt - lastChangeCheck.current) / 1000) + CHANGE_CHECK_OVERLAP_SECONDS
        let check = await db.Regi_CheckForChanges(JSON.stringify(reservationIds), client.UserName, lookBackSeconds)
        if (check?.length > 0 && check[0].result === false) {
          lastChangeCheck.current = checkStartedAt
          scheduleNext()
        } else if (check?.length > 0) {
          setUpdateCheck(true)
        } else {
          // The check itself failed (offline, webservice down) - that's not a change. Keep the
          // window open and try again; it used to show the update button here.
          scheduleNext()
        }
      }
    }
  }
  handleUpdateRef.current = handleUpdate

  // WHY!!!
  function handleStudentChange(data) {
    sortStudents(data, studentSort);
  }

  function sortStudents(data: any, sortBy: string) {
    const sortedStudents = data.sort((a, b) => {
      // To skip having to sort between ppl with tons of names,
      // just comparing the very first name and the last name
      const aa = a.firstname.split(' ')[0] + ', ' + a.lastname;
      const bb = b.firstname.split(' ')[0] + ', ' + b.lastname;

      if (sortBy === Sorting.ALPHA_ASC) {
        if (aa > bb) return 1;
        else if (aa < bb) return -1;
        return 0;
      } else {
        if (aa < bb) return 1;
        else if (aa > bb) return -1;
        return 0;
      }
    });
    setStudents(sortedStudents);
  }

  // Registration Methods
  //

  //
  const setActiveIndex = useCallback(
    (data: number) => {
      let l = students.data?.length;
      setRegistrationIndex(getIndex(data, l));
    },
    [students],
  );

  const handleMatchNavAction = useCallback(
    ({ actionType, target, lateMinutes }) => {
      let targetIndex = registrationIndex;

      const student = students.data.find((item) => item.sis_id === students.data[targetIndex].sis_id);

      if (student) {
        updateStudent(actionType, student, scheduleIndex, -1, lateMinutes);
      }
    },
    [registrationIndex, students],
  );

  const handleListMatchNavAction = useCallback(
    ({ actionType,tag_ids,textArea, lateMinutes } ) => {
      if (schedule.loading || students.loading) {
        return;
      }
      let jsonList: AllDayMarkingArr[] = [];
      // for each schedule item, loop thru students, if all of them are selected, use massMark(), else for each item use singleMark()
      Object.keys(listSelection.data).forEach((studentKey) => {
        const studentItem = listSelection.data[studentKey];
        Object.keys(studentItem.reservations).forEach((scheduleItemkey) => {
          const scheduleItem = studentItem.reservations[scheduleItemkey];
          if (scheduleItem !== undefined) {
            if (scheduleItem === true) {
              const type = getAppropriateType(actionType);
              jsonList.push({
                student_id: studentItem.sis_id,
                timeblock_id: scheduleItemkey,
                type,
                username: client.UserName,
                tag_ids: tag_ids,
                text: textArea,
                lateminutes: lateMinutes
              });
            }
          }
        });
      });
      dispatchListStudentAttendance(jsonList);
    },
    [students, listSelection, schedule],
  );

  // function dispatchListStudentAttendance(id: string | number, attending: AttendanceType, username: string, timeblockId: any) {
  function dispatchListStudentAttendance(jsonList: any) {
    // console.log(attending);
    // construct json from listSelection
    let defaultMinutes = systemSettings.find((s) => s.APP_Setting_ID === 47);
    updateListStudents(jsonList, client.UserName, moment(registrationQuery.date), 'TEACHER', defaultMinutes.value);
  }

  const handleMassMatchNavAction = useCallback(
    (actionType) => {
      massMarkEverybody(actionType);
    },
    [students],
  );

  function massMarkEverybody(actionType: ExtraMatchButtonType) {
    const timeblockId = schedule?.data[scheduleIndex]?.id;
    const { MISSING_ALL, ATTENDING_ALL, RESET_ALL } = ExtraMatchButtonType;
    let attending: AttendanceType | null = null;
    switch (actionType) {
      case MISSING_ALL:
        attending = AttendanceType.MISSING;
        break;
      case ATTENDING_ALL:
        attending = AttendanceType.ATTENDING;
        break;
      case RESET_ALL:
        attending = AttendanceType.UNKNOWN;
        break;
    }

    let enableLateMinutes = systemSettings.find((s) => s.APP_Setting_ID === 49);
    let defaultMinutes = systemSettings.find((s) => s.APP_Setting_ID === 47);

    massUpdateRegistration(
      timeblockId,
      attending,
      client?.UserName,
      getType(),
      registrationQuery.date,
      filterDate.from,
      filterDate.to,
      defaultMinutes.value,
    );
  }

  const studentSelectCallback = useCallback(
    (studentId) => {
      handleSelectedStudent(studentId);
    },
    [students],
  );

  useListener('onMatchListNavAction', handleListMatchNavAction);
  useListener('onMatchNavAction', handleMatchNavAction);
  useListener('onMassMatchNavAction', handleMassMatchNavAction);
  useListener('onStudentSelected', studentSelectCallback);

  function getAppropriateType(actionType: MatchActionType): AttendanceType {
    const { MISSING, ATTENDING, LATE, RESET } = MatchActionType;

    switch (actionType) {
      case MISSING:
        return AttendanceType.MISSING;
      case ATTENDING:
        return AttendanceType.ATTENDING;
      case LATE:
        return AttendanceType.LATE;
      case RESET:
        return AttendanceType.UNKNOWN;
      default:
        console.error('Action type is not supported: got - ' + actionType + ' (' + AttendanceType + ')');
    }
  }

  const updateStudent = (actionType, student, index = scheduleIndex, courseIndex = -1, lateMinutes = null) => {
    const timeblockId = schedule?.data[index]?.id;
    let attending = null as AttendanceType;
    attending = getAppropriateType(actionType);
    dispatchStudentAttendance(student.sis_id, attending, client?.UserName, timeblockId, courseIndex, lateMinutes);
  };

  const handleSelectedStudent = (id, duration?) => {
    setActiveIndex(students.data.findIndex((item) => item.sis_id === id));
    let item = students.data.find((item) => item.sis_id === id);
    setSelectedStudent(item.firstname, id, null, duration && duration > 0 ? duration : null);
  };

  function dispatchStudentAttendance(
    id: string | number,
    attending: AttendanceType,
    username: string,
    timeblockId: any,
    courseIndex: number = null,
    lateMinutes?: Number,
  ) {

    let defaultlateMinutes = systemSettings.find((s) => s.APP_Setting_ID === 47);

    let temp = lateMinutes ? lateMinutes : Number(defaultlateMinutes.value);

    updateRegistration(
      id,
      timeblockId,
      attending,
      username,
      courseIndex,
      moment(registrationQuery.date),
      filterDate.from,
      filterDate.to,
      temp,
    );

    if (!attending) {
      return;
    }
    if (registrationQuery.forType === 'STUDENT') {
      return;
    }
    if (registrationView === RegistrationViewType.SWIPE && attending === AttendanceType.UNKNOWN) {
      setActiveIndex(registrationIndex - 1);
    } else {
      setActiveIndex(registrationIndex + 1);
    }
  }

  return <RegistrationViewHandler handleSelectedStudent={handleSelectedStudent} />;
};

export default memo(RegistrationHandler);
// export default RegistrationHandler;
